import { useState, useMemo, useCallback } from "react";

export interface UsePaginationProps {
  totalItems: number;
  itemsPerPage: number;
  initialPage?: number;
}

export interface UsePaginationReturn {
  currentPage: number;
  totalPages: number;
  startIndex: number;
  endIndex: number;
  hasNextPage: boolean;
  hasPreviousPage: boolean;
  goToPage: (page: number) => void;
  goToNextPage: () => void;
  goToPreviousPage: () => void;
  getPageNumbers: () => number[];
  getVisiblePageNumbers: (maxVisible?: number) => (number | "ellipsis")[];
}

export function usePagination({
  totalItems,
  itemsPerPage,
  initialPage = 1,
}: UsePaginationProps): UsePaginationReturn {
  const [currentPage, setCurrentPage] = useState(initialPage);

  const totalPages = Math.ceil(totalItems / itemsPerPage);

  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = Math.min(startIndex + itemsPerPage, totalItems);

  const hasNextPage = currentPage < totalPages;
  const hasPreviousPage = currentPage > 1;

  const goToPage = useCallback(
    (page: number) => {
      if (page >= 1 && page <= totalPages) {
        setCurrentPage(page);
      }
    },
    [totalPages],
  );

  const goToNextPage = useCallback(() => {
    setCurrentPage((prev) => (prev < totalPages ? prev + 1 : prev));
  }, [totalPages]);

  const goToPreviousPage = useCallback(() => {
    setCurrentPage((prev) => (prev > 1 ? prev - 1 : prev));
  }, []);

  const getPageNumbers = useCallback(() => {
    return Array.from({ length: totalPages }, (_, i) => i + 1);
  }, [totalPages]);

  const getVisiblePageNumbers = useCallback(
    (maxVisible = 7) => {
      const pages: (number | "ellipsis")[] = [];

      if (totalPages <= maxVisible) {
        // show all pages if total is less than max visible
        return Array.from({ length: totalPages }, (_, i) => i + 1);
      }

      // always show first page
      pages.push(1);

      const startPage = Math.max(2, currentPage - Math.floor(maxVisible / 2));
      const endPage = Math.min(totalPages - 1, startPage + maxVisible - 3);

      // add ellipsis after first page if needed
      if (startPage > 2) {
        pages.push("ellipsis");
      }

      // add middle pages
      for (let i = startPage; i <= endPage; i++) {
        pages.push(i);
      }

      // add ellipsis before last page if needed
      if (endPage < totalPages - 1) {
        pages.push("ellipsis");
      }

      // always show last page if more than 1 page
      if (totalPages > 1) {
        pages.push(totalPages);
      }

      return pages;
    },
    [currentPage, totalPages],
  );

  return useMemo(
    () => ({
      currentPage,
      totalPages,
      startIndex,
      endIndex,
      hasNextPage,
      hasPreviousPage,
      goToPage,
      goToNextPage,
      goToPreviousPage,
      getPageNumbers,
      getVisiblePageNumbers,
    }),
    [
      currentPage,
      totalPages,
      startIndex,
      endIndex,
      hasNextPage,
      hasPreviousPage,
      goToPage,
      goToNextPage,
      goToPreviousPage,
      getPageNumbers,
      getVisiblePageNumbers,
    ],
  );
}
